import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Calculator, Receipt, MessageCircle } from 'lucide-react';
import { fadeUp, staggerContainer, viewport } from '../../lib/motion';
import { waLink } from '../../lib/whatsapp';

const inr = (n) => `₹${Number(n).toLocaleString('en-IN')}`;

export default function Calculators() {
  const [tab, setTab] = useState('tax');
  const [revenue, setRevenue] = useState(1200000);
  const [deductions, setDeductions] = useState(150000);
  const [bracket, setBracket] = useState(20);
  const [gstAmount, setGstAmount] = useState(500000);
  const [gstRate, setGstRate] = useState(18);

  const taxable = Math.max(0, revenue - deductions);
  const tax = Math.round(taxable * (bracket / 100));
  const gst = Math.round((gstAmount * gstRate) / 100);

  const labelCls = 'block text-sm text-slate-300 mb-2';
  const valCls = 'text-gold-light font-semibold';

  return (
    <section id="calculator" className="relative px-6 lg:px-10 py-28 bg-ink-800 overflow-hidden">
      <div className="absolute inset-0 -z-0 opacity-[0.06]">
        <img src="https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" alt="" className="w-full h-full object-cover" />
      </div>
      <div className="max-w-5xl mx-auto relative z-10">
        <motion.div variants={staggerContainer()} initial="hidden" whileInView="show" viewport={viewport} className="text-center max-w-2xl mx-auto mb-14">
          <motion.span variants={fadeUp} className="text-xs tracking-[0.2em] uppercase font-semibold text-gold-light">Interactive Tools</motion.span>
          <motion.h2 variants={fadeUp} className="font-display text-4xl sm:text-5xl text-white tracking-tight mt-3">Tax & GST Estimator</motion.h2>
          <motion.p variants={fadeUp} className="text-slate-400 mt-4 text-lg">Estimate your liabilities and GST in seconds — then talk to an expert.</motion.p>
        </motion.div>

        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport} className="bg-ink-900/80 backdrop-blur-xl border border-slate-800 rounded-3xl p-8 lg:p-12 shadow-2xl">
          <div className="flex gap-4 border-b border-slate-800 pb-6 mb-8">
            <button onClick={() => setTab('tax')} data-testid="calc-tab-tax" className={`flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition-colors ${tab === 'tax' ? 'bg-gold text-ink-900' : 'bg-ink-800 text-slate-300 hover:bg-ink-700'}`}>
              <Calculator className="w-4 h-4" strokeWidth={1.5} /> Income Tax
            </button>
            <button onClick={() => setTab('gst')} data-testid="calc-tab-gst" className={`flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition-colors ${tab === 'gst' ? 'bg-gold text-ink-900' : 'bg-ink-800 text-slate-300 hover:bg-ink-700'}`}>
              <Receipt className="w-4 h-4" strokeWidth={1.5} /> GST Calculator
            </button>
          </div>

          {tab === 'tax' ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center" data-testid="tax-calc-panel">
              <div className="space-y-7">
                <div>
                  <label className={labelCls}>Annual Income: <span className={valCls}>{inr(revenue)}</span></label>
                  <input type="range" min="100000" max="10000000" step="50000" value={revenue} onChange={(e) => setRevenue(+e.target.value)} data-testid="revenue-slider" className="w-full" />
                </div>
                <div>
                  <label className={labelCls}>Deductions: <span className={valCls}>{inr(deductions)}</span></label>
                  <input type="range" min="0" max="2000000" step="10000" value={deductions} onChange={(e) => setDeductions(+e.target.value)} data-testid="deductions-slider" className="w-full" />
                </div>
                <div>
                  <label className={labelCls}>Tax Slab: <span className={valCls}>{bracket}%</span></label>
                  <input type="range" min="5" max="42" step="1" value={bracket} onChange={(e) => setBracket(+e.target.value)} data-testid="bracket-slider" className="w-full" />
                </div>
              </div>
              <div className="bg-ink-800 border border-slate-800 p-8 rounded-2xl space-y-5">
                <h4 className="text-xs tracking-[0.2em] uppercase text-slate-400 font-semibold">Estimated Summary</h4>
                <div className="flex justify-between text-sm"><span className="text-slate-400">Net Taxable Income</span><span className="text-white font-semibold" data-testid="taxable-income-val">{inr(taxable)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-slate-400">Effective Rate</span><span className="text-white font-semibold">{bracket}%</span></div>
                <div className="pt-4 border-t border-slate-800 flex justify-between items-center">
                  <span className="text-white font-medium">Estimated Tax</span>
                  <span className="font-display text-3xl text-gold-light" data-testid="estimated-tax-val">{inr(tax)}</span>
                </div>
                <a href={waLink('Hello SN Associates, I used your income tax estimator and would like expert advice on my tax planning.')} target="_blank" rel="noopener noreferrer" data-testid="calc-whatsapp-tax" className="w-full flex items-center justify-center gap-2 py-3.5 rounded-full bg-[#25D366] hover:bg-[#20bd5a] text-white font-semibold text-sm transition-colors"><MessageCircle className="w-4 h-4" strokeWidth={1.5} /> Consult a Tax Expert</a>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center" data-testid="gst-calc-panel">
              <div className="space-y-7">
                <div>
                  <label className={labelCls}>Invoice Amount: <span className={valCls}>{inr(gstAmount)}</span></label>
                  <input type="range" min="10000" max="5000000" step="10000" value={gstAmount} onChange={(e) => setGstAmount(+e.target.value)} data-testid="gst-amount-slider" className="w-full" />
                </div>
                <div>
                  <label className={labelCls}>GST Rate: <span className={valCls}>{gstRate}%</span></label>
                  <div className="grid grid-cols-4 gap-3">
                    {[5, 12, 18, 28].map((r) => (
                      <button key={r} onClick={() => setGstRate(r)} data-testid={`gst-rate-${r}`} className={`py-2.5 rounded-xl text-sm font-semibold transition-colors ${gstRate === r ? 'bg-gold text-ink-900' : 'bg-ink-800 text-slate-300 border border-slate-700'}`}>{r}%</button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="bg-ink-800 border border-slate-800 p-8 rounded-2xl space-y-5">
                <h4 className="text-xs tracking-[0.2em] uppercase text-slate-400 font-semibold">GST Breakdown</h4>
                <div className="flex justify-between text-sm"><span className="text-slate-400">Base Amount</span><span className="text-white font-semibold">{inr(gstAmount)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-slate-400">GST ({gstRate}%)</span><span className="text-white font-semibold" data-testid="gst-rate-val">{inr(gst)}</span></div>
                <div className="pt-4 border-t border-slate-800 flex justify-between items-center">
                  <span className="text-white font-medium">Total Invoice</span>
                  <span className="font-display text-3xl text-gold-light" data-testid="total-invoice-val">{inr(gstAmount + gst)}</span>
                </div>
                <a href={waLink('Hello SN Associates, I used your GST calculator and need help with GST filing/compliance.')} target="_blank" rel="noopener noreferrer" data-testid="calc-whatsapp-gst" className="w-full flex items-center justify-center gap-2 py-3.5 rounded-full bg-[#25D366] hover:bg-[#20bd5a] text-white font-semibold text-sm transition-colors"><MessageCircle className="w-4 h-4" strokeWidth={1.5} /> Get GST Filing Support</a>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
}
